"use client";

import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { useRef } from "react";

import { safePlayVideo } from "@/lib/safe-video-play";

gsap.registerPlugin(ScrollTrigger);

const ECOSYSTEM_VIDEO_SRC = "/images/dm-ecosystem-section.mp4";

export function DmEcoSystemSection() {
  const scope = useRef<HTMLElement | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const maskRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useGSAP(
    () => {
      const viewport = viewportRef.current;
      const mask = maskRef.current;
      const video = videoRef.current;
      if (!mask || !viewport || !video) return;

      const mm = gsap.matchMedia();

      mm.add(
        {
          reduceMotion: "(prefers-reduced-motion: reduce)",
          isDesktop: "(min-width: 1024px)",
          isMobile: "(max-width: 1023px)",
        },
        (context) => {
          const { reduceMotion } = context?.conditions as {
            reduceMotion: boolean;
          };

          if (reduceMotion) {
            gsap.set(mask, {
              width: "100%",
              height: "100%",
              borderRadius: "1.9rem",
            });
          } else {
            gsap.fromTo(
              mask,
              { width: 500, height: 500, borderRadius: "100%" },
              {
                width: "100%",
                height: "100%",
                borderRadius: "1.9rem",
                ease: "none",
                scrollTrigger: {
                  trigger: viewport,
                  start: "center 90%",
                  end: "center 70%",
                  scrub: 1.3,
                  invalidateOnRefresh: true,
                },
              },
            );
          }
        },
      );

      const syncVars = () => {
        viewport.style.setProperty("--screen-w", `${viewport.offsetWidth}px`);
        viewport.style.setProperty("--screen-h", `${viewport.offsetHeight}px`);
      };
      syncVars();
      window.addEventListener("resize", syncVars);

      const playVideo = () => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        safePlayVideo(video);
      };
      const pauseVideo = () => {
        video.pause();
      };

      const videoScrollTrigger = ScrollTrigger.create({
        trigger: viewport,
        start: "top 80%",
        end: "bottom 20%",
        onToggle: (self) => {
          if (self.isActive) playVideo();
          else pauseVideo();
        },
      });

      return () => {
        window.removeEventListener("resize", syncVars);
        video.pause();
        videoScrollTrigger.kill();
        mm.revert();
      };
    },
    { scope, dependencies: [] },
  );

  return (
    <section
      ref={scope}
      className="relative min-h-[1200px] overflow-visible bg-[#1b2631] pt-16 pb-20 font-sans text-white md:pt-20"
      aria-labelledby="digital-growth-heading"
    >
      <div className="site-content pt-12 pb-0 md:pt-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-16 xl:gap-20">
          <div className="max-w-[680px] lg:col-span-7">
            <p className="text-sm font-medium tracking-wide text-[#8fa8bf] md:text-base">
              Digital marketing that generates ROI
            </p>
            <h2
              id="digital-growth-heading"
              className="mt-4 text-4xl leading-[0.8em] tracking-tight text-white sm:text-5xl lg:text-6xl xl:text-7xl"
            >
              Digital marketing eco-systems that tracks and{" "}
              <span className="text-[#ff5c00]">hunts your customers</span>
            </h2>
          </div>

          <div className="lg:col-span-5">
            <p className="text-4xl font-medium leading-none text-[#ff5c00] md:text-5xl">
              *
            </p>
            <hr className="mt-4 max-w-full border-0 border-t border-white/90" />
            <p className="mt-6 text-base leading-relaxed text-white/95 md:text-lg">
              Every customer journey is different, so your marketing system should be too. We build connected
              digital ecosystems that find, follow, and respond to real customer behaviour across channels,
              turning data into insight and intent into action, so your brand shows up at the right time, in the
              right place, with the right message.
            </p>
          </div>
        </div>
      </div>

      <div className="site-content relative mt-6 sm:h-0 md:mt-16">
        <div className="relative left-0 right-0 top-0 z-10 w-full md:left-1/2 md:right-auto md:w-[min(30rem,76vw)] md:-translate-x-1/2 lg:w-[min(42rem,72vw)]">
          <div className="max-md:rounded-2xl max-md:p-0 sm:max-md:rounded-3xl sm:max-md:p-2.5 rounded-[2.5rem] p-2.5 md:rounded-[2.75rem] md:p-3 lg:rounded-[3rem] lg:p-3.5">
            <div
              ref={viewportRef}
              className="relative aspect-9/17 w-full overflow-hidden max-md:rounded-[1.35rem] rounded-[1.85rem] md:rounded-[2.125rem] lg:rounded-4xl"
            >
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div
                  ref={maskRef}
                  className="flex items-center justify-center overflow-hidden will-change-[width,height,border-radius]"
                >
                  <div
                    className="relative shrink-0 [&_video]:pointer-events-none"
                    style={{
                      width: "var(--screen-w, 100%)",
                      height: "var(--screen-h, 100%)",
                    }}
                  >
                    <video
                      ref={videoRef}
                      className="absolute inset-0 h-full w-full object-cover"
                      src={ECOSYSTEM_VIDEO_SRC}
                      loop
                      muted
                      playsInline
                      preload="metadata"
                      aria-label="Digital marketing ecosystem video"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
